import type { ContactInfoRequirement } from './contactInfoRequirement';

/** Which inputs the contact form renders. */
export type ContactInfoFields = 'email' | 'nameAndEmail';

/**
 * Where `fieldsShown` came from: a Statsig assignment (`experiment`), the email-only fallback when
 * Statsig gave no assignment (`fallback`: failed, timed out, or not running), the non-prod
 * `?contactFields=` override (`qaOverride`), or a flow outside the test (`notInTest`). Only
 * `experiment` events belong in the A/B comparison.
 */
export type ContactInfoFieldsSource = 'experiment' | 'fallback' | 'notInTest' | 'qaOverride';

/**
 * What a contact step showed the customer. Every contact Heap event (Viewed / Skipped /
 * Completed, in both sign-in and start-visit onboarding) carries it, so conversion can be split
 * by fields shown × optional badge, by which fields were required, and by whether the visible
 * fields arrived already filled in.
 */
export type ContactInfoDisplay = {
  /** The email input was filled in from the account or invite when the step opened. */
  emailPrefilled: boolean;
  /** Continue stays disabled without an email (individual and enterprise sign-up). */
  emailRequired: boolean;
  fieldsShown: ContactInfoFields;
  fieldsSource: ContactInfoFieldsSource;
  /** A visible name input was filled in when the step opened — always false in email-only mode. */
  namePrefilled: boolean;
  /** Continue stays disabled without first and last name (enterprise sign-up). Always false when
   *  the name inputs are hidden. */
  nameRequired: boolean;
  optionalBadgeShown: boolean;
};

export function contactInfoDisplay({
  defaultValues,
  fields,
  fieldsSource,
  requirement,
  showOptionalBadge,
}: {
  defaultValues: { email?: string; firstName?: string; lastName?: string };
  fields: ContactInfoFields;
  fieldsSource: ContactInfoFieldsSource;
  requirement: ContactInfoRequirement;
  showOptionalBadge: boolean;
}): ContactInfoDisplay {
  return {
    emailPrefilled: !!defaultValues.email,
    emailRequired: requirement !== 'none',
    fieldsShown: fields,
    fieldsSource,
    namePrefilled:
      fields === 'nameAndEmail' && (!!defaultValues.firstName || !!defaultValues.lastName),
    nameRequired: fields === 'nameAndEmail' && requirement === 'all',
    optionalBadgeShown: showOptionalBadge,
  };
}

/**
 * What a contact step's Completed event says was submitted. `added*` counts any visible field
 * that had a value on submit, whether the customer typed it or it was pre-filled; `*Typed` counts
 * only values the customer entered or changed. Pair with `emailPrefilled` / `namePrefilled` to
 * tell "kept what was on file" from "gave us something new".
 */
export type ContactInfoSubmission = {
  addedEmail: boolean;
  addedName: boolean;
  emailTyped: boolean;
  nameTyped: boolean;
};

export function contactInfoSubmission({
  defaultValues,
  fields,
  values,
}: {
  defaultValues: { email?: string; firstName?: string; lastName?: string };
  fields: ContactInfoFields;
  values: { email: string; firstName: string; lastName: string };
}): ContactInfoSubmission {
  // Email-only mode passes the account's names through untouched; they weren't on screen.
  const showsName = fields === 'nameAndEmail';
  const isTyped = (value: string, prefilled = '') => !!value && value !== prefilled.trim();
  return {
    addedEmail: !!values.email,
    addedName: showsName && (!!values.firstName || !!values.lastName),
    emailTyped: isTyped(values.email, defaultValues.email),
    nameTyped:
      showsName &&
      (isTyped(values.firstName, defaultValues.firstName) ||
        isTyped(values.lastName, defaultValues.lastName)),
  };
}
